/**
 * Shared storage primitive for a tab-scoped Bearer token with a CLIENT-ENFORCED
 * expiry, independent of whatever lifetime the token carries server-side.
 *
 * WHY THIS EXISTS
 * ----------------
 * `impersonation-token.ts` and `admin-tab-token.ts` both store a real,
 * server-valid session token in `sessionStorage` so a tab can keep
 * authenticating as itself without depending on the shared session cookie
 * (which another tab's impersonate call can rewrite). `sessionStorage`
 * surviving a reload is the whole point — but it ALSO survives
 * `document.cookie` being cleared, which broke a real, pre-existing guarantee:
 * `admin.spec.ts`'s "impersonation does not survive losing every cookie" —
 * clearing every cookie used to leave production web (cookie-only) with no
 * credential at all. A tab holding one of these tokens now keeps one.
 *
 * There is no reliable way to detect "the cookie was just cleared" from JS —
 * no storage event fires for it, and `document.cookie` never exposed the
 * HttpOnly session cookie to begin with, so its absence from `document.cookie`
 * proves nothing either way. What IS achievable: bound how long a token found
 * in `sessionStorage` remains USABLE, independent of the tab staying open —
 * so a cookie-clear-and-walk-away is caught the next time this tab is used,
 * rather than never.
 *
 * `TAB_TOKEN_MAX_AGE_MS` is a CLIENT-side bound only. It does not revoke the
 * underlying server session (impersonation already carries its own 3-hour
 * server TTL via `impersonationSessionDuration`; the admin's own token is
 * still the real, long-lived session value). It exists to shrink the window
 * during which a tab that no longer holds any cookie can still act, not to
 * eliminate it.
 */
const TAB_TOKEN_MAX_AGE_MS = 20 * 60 * 1000;

interface StoredTabToken {
  token: string;
  /** `Date.now()` this token becomes unusable, client-side. */
  expiresAt: number;
}

function isStoredTabToken(value: unknown): value is StoredTabToken {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as StoredTabToken).token === 'string' &&
    typeof (value as StoredTabToken).expiresAt === 'number'
  );
}

/** Read a token written by `setExpiringTabToken`, or `undefined` if absent/expired. */
export function getExpiringTabToken(key: string): string | undefined {
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(key);
  } catch {
    // Storage unavailable (private mode, blocked site data) — the tab falls
    // back to the normal cookie/native-bearer path.
    return undefined;
  }
  if (!raw) return undefined;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    parsed = undefined;
  }

  if (!isStoredTabToken(parsed) || Date.now() >= parsed.expiresAt) {
    // Malformed (e.g. an older format) or expired — either way, stale.
    // Cleaned up here so a later read isn't paying to re-discover the same
    // thing, and so the entry doesn't linger as dead weight in storage.
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // Storage unavailable — nothing to clean up.
    }
    return undefined;
  }
  return parsed.token;
}

/**
 * Store `token`, usable for `maxAgeMs` from now (default
 * `TAB_TOKEN_MAX_AGE_MS`). A token whose server session has its own, shorter
 * or comparable lifetime can pass that instead — see `impersonation-token.ts`.
 */
export function setExpiringTabToken(
  key: string,
  token: string,
  maxAgeMs: number = TAB_TOKEN_MAX_AGE_MS
): void {
  try {
    const value: StoredTabToken = {
      token,
      expiresAt: Date.now() + maxAgeMs,
    };
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Nothing to do — see above.
  }
}

export function clearExpiringTabToken(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Nothing to do — see above.
  }
}
